import React from 'react';
import './Skeleton.css';

/** Single shimmer bone — building block for layout-matched skeletons. */
export function Skeleton({
  width = '100%',
  height = '0.9rem',
  className = '',
  rounded = 'default',
  style,
  ...rest
}) {
  const roundClass =
    rounded === 'circle' ? 'sk--circle' : rounded === 'pill' ? 'sk--pill' : rounded === 'block' ? 'sk--block' : '';

  return (
    <span
      className={`sk ${roundClass} ${className}`.trim()}
      style={{ width, height, ...style }}
      aria-hidden
      {...rest}
    />
  );
}

export function SkeletonStat() {
  return (
    <div className="sk-stat">
      <Skeleton width="42%" height="0.75rem" />
      <Skeleton width="68%" height="1.35rem" />
    </div>
  );
}

export function SkeletonPanel({ glow, children, style, className = '' }) {
  return (
    <div
      className={`sk-panel${glow ? ' sk-panel--glow' : ''} ${className}`.trim()}
      style={style}
    >
      {children}
    </div>
  );
}

export function SkeletonTable({ rows = 4, cols = 4 }) {
  return (
    <div className="table-wrap">
      <table className="sk-table table">
        <tbody>
          {Array.from({ length: rows }).map((_, r) => (
            <tr key={r}>
              {Array.from({ length: cols }).map((__, c) => (
                <td key={c}>
                  <Skeleton
                    width={c === 0 ? '70%' : c === cols - 1 ? '55%' : '60%'}
                    height="0.8rem"
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Dashboard: hero panel + 5 stats + purchases table */
export function DashboardSkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading dashboard">
      <SkeletonPanel glow>
        <Skeleton width="5.5rem" height="1.35rem" rounded="pill" />
        <Skeleton width="12rem" height="1.55rem" style={{ marginTop: '0.85rem' }} />
        <Skeleton width="9rem" height="0.85rem" style={{ marginTop: '0.55rem' }} />
        <div className="sk-stack">
          <Skeleton width="7.5rem" height="2.5rem" rounded="block" />
          <Skeleton width="7.5rem" height="2.5rem" rounded="block" />
        </div>
      </SkeletonPanel>

      <div className="grid" style={{ marginTop: '1rem' }}>
        {Array.from({ length: 5 }).map((_, i) => (
          <SkeletonStat key={i} />
        ))}
      </div>

      <SkeletonPanel style={{ marginTop: '1rem' }}>
        <Skeleton width="10rem" height="1.15rem" style={{ marginBottom: '1rem' }} />
        <SkeletonTable rows={4} cols={4} />
      </SkeletonPanel>
    </div>
  );
}

/** Plans: info panel with 3 stats + purchase form panel */
export function PlansSkeleton() {
  return (
    <div className="panel-grid" aria-busy="true" aria-label="Loading plans">
      <SkeletonPanel glow>
        <Skeleton width="9rem" height="1.35rem" rounded="pill" />
        <Skeleton width="14rem" height="1.5rem" style={{ marginTop: '0.85rem' }} />
        <Skeleton width="90%" height="0.85rem" style={{ marginTop: '0.65rem' }} />
        <Skeleton width="70%" height="0.85rem" style={{ marginTop: '0.4rem' }} />
        <div className="grid" style={{ marginTop: '1rem' }}>
          <SkeletonStat />
          <SkeletonStat />
          <SkeletonStat />
        </div>
      </SkeletonPanel>
      <SkeletonPanel>
        <Skeleton width="6rem" height="1.15rem" style={{ marginBottom: '1rem' }} />
        <Skeleton width="5rem" height="0.75rem" />
        <Skeleton width="100%" height="2.6rem" rounded="block" style={{ marginTop: '0.4rem' }} />
        <Skeleton width="100%" height="2.6rem" rounded="block" style={{ marginTop: '1rem' }} />
      </SkeletonPanel>
    </div>
  );
}

/** Wallet: balances + unclaimed + claim form + history */
export function WalletSkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading wallet">
      <SkeletonPanel glow>
        <Skeleton width="8rem" height="1.35rem" rounded="pill" />
        <div className="sk-row" style={{ marginTop: '0.85rem', justifyContent: 'space-between' }}>
          <Skeleton width="11rem" height="1.45rem" />
          <Skeleton width="6.5rem" height="2.4rem" rounded="block" />
        </div>
        <div className="wallet-balances">
          <SkeletonStat />
          <SkeletonStat />
        </div>
      </SkeletonPanel>

      <div className="panel-grid panel-grid--span-last">
        <SkeletonPanel>
          <Skeleton width="5.5rem" height="1.35rem" rounded="pill" />
          <Skeleton width="10rem" height="1.4rem" style={{ marginTop: '0.75rem' }} />
          <Skeleton width="80%" height="0.8rem" style={{ marginTop: '0.5rem' }} />
          <div className="wallet-balances">
            <SkeletonStat />
            <SkeletonStat />
          </div>
          <div className="sk-stack">
            <Skeleton width="11rem" height="2.5rem" rounded="block" />
          </div>
        </SkeletonPanel>

        <SkeletonPanel>
          <Skeleton width="7rem" height="1.15rem" style={{ marginBottom: '1rem' }} />
          <Skeleton width="6rem" height="0.75rem" />
          <Skeleton width="100%" height="2.6rem" rounded="block" style={{ marginTop: '0.4rem' }} />
          <Skeleton width="100%" height="2.5rem" rounded="block" style={{ marginTop: '1rem' }} />
        </SkeletonPanel>

        <SkeletonPanel>
          <Skeleton width="8rem" height="1.15rem" style={{ marginBottom: '1rem' }} />
          <SkeletonTable rows={4} cols={4} />
        </SkeletonPanel>
      </div>
    </div>
  );
}

/** Income: cap panel + report table */
export function IncomeSkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading income">
      <SkeletonPanel glow>
        <Skeleton width="3.5rem" height="1.35rem" rounded="pill" />
        <Skeleton width="12rem" height="1.45rem" style={{ marginTop: '0.75rem' }} />
        <Skeleton width="92%" height="0.8rem" style={{ marginTop: '0.55rem' }} />
        <Skeleton width="75%" height="0.8rem" style={{ marginTop: '0.35rem' }} />
        <div className="wallet-balances">
          <SkeletonStat />
          <SkeletonStat />
          <SkeletonStat />
          <SkeletonStat />
        </div>
        <Skeleton width="100%" height="0.55rem" rounded="pill" style={{ marginTop: '1rem' }} />
      </SkeletonPanel>

      <SkeletonPanel style={{ marginTop: '1rem' }}>
        <div className="sk-row" style={{ justifyContent: 'space-between', marginBottom: '1rem' }}>
          <Skeleton width="9rem" height="1.2rem" />
          <div className="sk-stack" style={{ marginTop: 0 }}>
            <Skeleton width="4rem" height="2rem" rounded="block" />
            <Skeleton width="4.5rem" height="2rem" rounded="block" />
            <Skeleton width="4.5rem" height="2rem" rounded="block" />
            <Skeleton width="5rem" height="2rem" rounded="block" />
          </div>
        </div>
        <SkeletonTable rows={6} cols={6} />
      </SkeletonPanel>
    </div>
  );
}

/** Support: header + 2 cards */
export function SupportSkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading support">
      <SkeletonPanel glow>
        <Skeleton width="5rem" height="1.35rem" rounded="pill" />
        <Skeleton width="11rem" height="1.45rem" style={{ marginTop: '0.75rem' }} />
        <Skeleton width="88%" height="0.85rem" style={{ marginTop: '0.55rem' }} />
      </SkeletonPanel>
      <div className="support-grid">
        {[0, 1].map((i) => (
          <div key={i} className="support-card" style={{ pointerEvents: 'none' }}>
            <Skeleton width="3rem" height="3rem" rounded="block" />
            <div style={{ flex: 1 }}>
              <Skeleton width="40%" height="1.1rem" />
              <Skeleton width="70%" height="0.8rem" style={{ marginTop: '0.5rem' }} />
              <Skeleton width="35%" height="0.75rem" style={{ marginTop: '0.85rem' }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Public marketing site — matches hero + strip + about */
export function PublicSiteSkeleton() {
  return (
    <div className="public" aria-busy="true" aria-label="Loading">
      <header className="public__nav">
        <div className="sk-row">
          <Skeleton width="2.15rem" height="2.15rem" rounded="block" />
          <Skeleton width="5rem" height="1.1rem" />
        </div>
        <div className="sk-row">
          <Skeleton width="2.4rem" height="2.4rem" rounded="block" />
          <Skeleton width="7.5rem" height="2.5rem" rounded="block" />
        </div>
      </header>
      <section className="public__hero">
        <div className="public__hero-bg" aria-hidden />
        <div className="public__hero-copy" style={{ width: 'min(28rem, 100%)' }}>
          <Skeleton width="8rem" height="0.75rem" style={{ margin: '0 auto' }} />
          <Skeleton width="70%" height="3.5rem" style={{ margin: '1rem auto 0' }} />
          <Skeleton width="90%" height="0.9rem" style={{ margin: '1.15rem auto 0' }} />
          <Skeleton width="75%" height="0.9rem" style={{ margin: '0.4rem auto 0' }} />
          <div style={{ marginTop: '1.75rem', display: 'flex', justifyContent: 'center' }}>
            <Skeleton width="9rem" height="2.8rem" rounded="block" />
          </div>
        </div>
      </section>
      <section className="public__strip">
        {[0, 1, 2].map((i) => (
          <div key={i}>
            <Skeleton width="40%" height="0.7rem" style={{ margin: '0 auto' }} />
            <Skeleton width="55%" height="1.05rem" style={{ margin: '0.55rem auto 0' }} />
          </div>
        ))}
      </section>
    </div>
  );
}

/** Compact connect-gate panel skeleton */
export function ConnectGateSkeleton() {
  return (
    <SkeletonPanel glow aria-busy="true" aria-label="Loading">
      <Skeleton width="5rem" height="1.35rem" rounded="pill" />
      <Skeleton width="16rem" height="1.45rem" style={{ marginTop: '0.75rem' }} />
      <Skeleton width="90%" height="0.85rem" style={{ marginTop: '0.55rem' }} />
      <div className="sk-stack">
        <Skeleton width="9rem" height="2.5rem" rounded="block" />
      </div>
    </SkeletonPanel>
  );
}

/** Team: summary stats + table */
export function TeamSkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading team">
      <SkeletonPanel glow>
        <Skeleton width="4rem" height="1.35rem" rounded="pill" />
        <Skeleton width="10rem" height="1.45rem" style={{ marginTop: '0.75rem' }} />
        <Skeleton width="80%" height="0.85rem" style={{ marginTop: '0.55rem' }} />
        <div className="wallet-balances">
          <SkeletonStat />
          <SkeletonStat />
          <SkeletonStat />
          <SkeletonStat />
        </div>
      </SkeletonPanel>
      <SkeletonPanel style={{ marginTop: '1rem' }}>
        <div className="sk-row" style={{ marginBottom: '1rem', gap: '0.5rem' }}>
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} width="4.5rem" height="2rem" rounded="block" />
          ))}
        </div>
        <SkeletonTable rows={6} cols={5} />
      </SkeletonPanel>
    </div>
  );
}

/** Invite: link panel + steps + FAQ */
export function InviteSkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading invite">
      <SkeletonPanel glow>
        <Skeleton width="4.5rem" height="1.35rem" rounded="pill" />
        <Skeleton width="14rem" height="1.45rem" style={{ marginTop: '0.75rem' }} />
        <Skeleton width="85%" height="0.85rem" style={{ marginTop: '0.55rem' }} />
        <div className="invite-box" style={{ marginTop: '1.25rem' }}>
          <Skeleton width="100%" height="3rem" rounded="block" style={{ flex: '1 1 240px' }} />
          <Skeleton width="7.5rem" height="2.6rem" rounded="block" />
        </div>
      </SkeletonPanel>
      <div className="feature-grid" style={{ marginTop: '1rem' }}>
        {[0, 1, 2].map((i) => (
          <SkeletonPanel key={i}>
            <Skeleton width="2rem" height="2rem" rounded="block" />
            <Skeleton width="50%" height="1rem" style={{ marginTop: '0.75rem' }} />
            <Skeleton width="90%" height="0.75rem" style={{ marginTop: '0.5rem' }} />
          </SkeletonPanel>
        ))}
      </div>
      <div className="panel-grid">
        <SkeletonPanel>
          <Skeleton width="4rem" height="1.2rem" style={{ marginBottom: '1rem' }} />
          {[0, 1, 2, 3].map((i) => (
            <Skeleton
              key={i}
              width="100%"
              height="2.8rem"
              rounded="block"
              style={{ marginBottom: '0.55rem' }}
            />
          ))}
        </SkeletonPanel>
        <SkeletonPanel>
          <Skeleton width="10rem" height="1.2rem" style={{ marginBottom: '1rem' }} />
          <Skeleton width="100%" height="0.85rem" style={{ marginBottom: '0.5rem' }} />
          <Skeleton width="92%" height="0.85rem" style={{ marginBottom: '0.5rem' }} />
          <Skeleton width="88%" height="0.85rem" style={{ marginBottom: '0.5rem' }} />
          <Skeleton width="80%" height="0.85rem" />
        </SkeletonPanel>
      </div>
    </div>
  );
}
